import { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { Upload, X, FileText, Image, Check } from "lucide-react";

interface FileUploadProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
  accept?: string;
  multiple?: boolean;
  label?: string;
  maxSize?: number;
}

export function FileUpload({
  files,
  onFilesChange,
  accept,
  multiple = true,
  label = "Drop files here or click to browse",
  maxSize = 50 * 1024 * 1024, // 50MB default
}: FileUploadProps) {
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      console.log(
        "📄 Dropped files:",
        acceptedFiles.map((f) => ({ name: f.name, type: f.type, size: f.size }))
      );

      if (multiple) {
        const newFiles = [...files, ...acceptedFiles];
        console.log(
          "📄 All files after drop:",
          newFiles.map((f) => f.name)
        );
        onFilesChange(newFiles);
      } else {
        onFilesChange(acceptedFiles.slice(0, 1));
      }
    },
    [files, onFilesChange, multiple]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: accept
      ? accept.split(",").reduce((acc, type) => {
          acc[type.trim()] = [];
          return acc;
        }, {} as Record<string, string[]>)
      : undefined,
    multiple,
    maxSize,
    onDropRejected: (rejectedFiles) => {
      console.warn("❌ Rejected files:", rejectedFiles);
      const errors = rejectedFiles.map(
        (f) => `${f.file.name}: ${f.errors.map((e) => e.message).join(", ")}`
      );
      alert(`Some files were rejected:\n${errors.join("\n")}`);
    },
    onError: (error) => {
      console.error("❌ Dropzone error:", error);
      alert("Error uploading files: " + error.message);
    },
  });

  const removeFile = (index: number) => {
    console.log("🗑️ Removing file:", files[index].name);
    const newFiles = files.filter((_, i) => i !== index);
    console.log(
      "📄 Remaining files:",
      newFiles.map((f) => f.name)
    );
    onFilesChange(newFiles);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getFileIcon = (file: File) => {
    if (file.type.startsWith("image/")) {
      return <Image className="w-5 h-5 text-violet-500" />;
    }
    return <FileText className="w-5 h-5 text-blue-500" />;
  };

  return (
    <div className="space-y-4">
      <div
        {...getRootProps()}
        className={`
          relative border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all duration-300 group
          ${
            isDragActive
              ? "border-blue-500 bg-blue-50 dark:bg-blue-900/30 scale-[1.02]"
              : "border-slate-300 dark:border-slate-600 hover:border-blue-400 hover:bg-slate-50 dark:hover:bg-slate-800"
          }
        `}
      >
        <input {...getInputProps()} />
        
        <div className={`
          w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center transition-all duration-300
          ${isDragActive 
            ? "bg-blue-100 dark:bg-blue-900/50 scale-110" 
            : "bg-slate-100 dark:bg-slate-700 group-hover:bg-blue-50 dark:group-hover:bg-blue-900/30 group-hover:scale-105"
          }
        `}>
          <Upload className={`
            w-7 h-7 transition-colors duration-300
            ${isDragActive ? "text-blue-600 dark:text-blue-400" : "text-slate-400 group-hover:text-blue-500"}
          `} />
        </div>
        
        <p className={`
          text-sm font-medium mb-1 transition-colors duration-300
          ${isDragActive ? "text-blue-700 dark:text-blue-300" : "text-slate-700 dark:text-slate-300"}
        `}>
          {isDragActive ? "Drop files here..." : label}
        </p>
        
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {accept && <span>Accepted: {accept.split(",").map(a => a.trim().replace(".", "").toUpperCase()).join(", ")}</span>}
          {accept && " • "}
          Max {formatFileSize(maxSize)}
        </p>
      </div>

      {files.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              Uploaded Files
            </h4>
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded-full">
              {files.length} file{files.length > 1 ? "s" : ""}
            </span>
          </div>
          
          <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
            {files.map((file, index) => (
              <div
                key={`${file.name}-${index}`}
                className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-all duration-200 group"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 bg-white dark:bg-slate-700 rounded-lg flex items-center justify-center shadow-sm">
                    {getFileIcon(file)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                      {file.name}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                      <span>{formatFileSize(file.size)}</span>
                      <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                        <Check className="w-3 h-3" />
                        Ready
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile(index);
                  }}
                  className="ml-2 p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                  title="Remove file"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
